import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetIdentity } from "ra-core";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { useIsMobile } from "@/hooks/use-mobile";
import { getSupabaseClient } from "../providers/supabase/supabase";
import { introStepFor, shouldShowIntro } from "./onboardingLogic";
import type { OnboardingStep, Story } from "./types";

type OnboardingContextValue = {
  seen: ReadonlySet<OnboardingStep>;
  loaded: boolean;
  introOpen: boolean;
  // « Revoir la présentation » ; `origin` reçoit le focus à la fermeture si l'élément cliqué a disparu (menu fermé).
  openIntro: (origin?: HTMLElement | null) => void;
  introOrigin: () => HTMLElement | null;
  closeIntro: () => void; // note l'étape de l'appareil, ferme
  markSeen: (step: OnboardingStep) => void;
  resetTips: () => Promise<void>; // « Réafficher les aides des écrans »
};

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

/** Ce que le compte a vu de la présentation, lu dans la base (pas dans le navigateur : il suit le compte). */
export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const isMobile = useIsMobile();
  const { identity } = useGetIdentity();
  const queryClient = useQueryClient();
  const [forced, setForced] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const origin = useRef<HTMLElement | null>(null);

  // La clé porte l'identité : changer de compte dans le même onglet ne réutilise pas les étapes de l'autre.
  const viewsKey = useMemo(() => ["onboarding_views", identity?.id], [identity?.id]);

  const { data: rows, isSuccess } = useQuery({
    queryKey: viewsKey,
    enabled: identity?.id != null,
    queryFn: async (): Promise<OnboardingStep[]> => {
      const { data, error } = await getSupabaseClient()
        .from("onboarding_views")
        .select("step");
      if (error) throw error;
      return (data ?? []).map((row) => row.step as OnboardingStep);
    },
  });
  const seen = useMemo(() => new Set(rows ?? []), [rows]);

  const markSeen = useCallback(
    (step: OnboardingStep) => {
      // Sans identité connue, rien n'est écrit : la clé du cache serait fausse.
      if (identity?.id == null) return;
      // Mise à jour immédiate seulement si les étapes sont déjà chargées.
      if (queryClient.getQueryData<OnboardingStep[]>(viewsKey) !== undefined) {
        queryClient.setQueryData<OnboardingStep[]>(viewsKey, (old = []) =>
          old.includes(step) ? old : [...old, step],
        );
      }
      // Insertion qui ignore les doublons (la table n'accorde pas la mise à jour).
      // Le `then` envoie la requête (le builder est paresseux). Un échec n'est pas montré :
      // le cache est relu, la séquence ou la bulle reviendra, sans gêner l'utilisateur.
      void getSupabaseClient()
        .from("onboarding_views")
        .upsert({ step }, { onConflict: "user_id,step", ignoreDuplicates: true })
        .then(({ error }) => {
          if (error) void queryClient.invalidateQueries({ queryKey: viewsKey });
        });
    },
    [queryClient, viewsKey, identity?.id],
  );

  const closeIntro = useCallback(() => {
    markSeen(introStepFor(isMobile));
    setForced(false);
    setDismissed(true);
  }, [isMobile, markSeen]);

  const openIntro = useCallback((from?: HTMLElement | null) => {
    origin.current = from ?? null;
    setDismissed(false);
    setForced(true);
  }, []);

  const resetTips = useCallback(async () => {
    const { error } = await getSupabaseClient()
      .from("onboarding_views")
      .delete()
      .like("step", "tip:%");
    if (error) throw error;
    await queryClient.invalidateQueries({ queryKey: viewsKey });
  }, [queryClient, viewsKey]);

  const introOrigin = useCallback(() => origin.current, []);

  const introOpen =
    forced || (isSuccess && !dismissed && shouldShowIntro(seen, isMobile));

  const value = useMemo(
    () => ({
      seen,
      loaded: isSuccess,
      introOpen,
      openIntro,
      introOrigin,
      closeIntro,
      markSeen,
      resetTips,
    }),
    [seen, isSuccess, introOpen, openIntro, introOrigin, closeIntro, markSeen, resetTips],
  );
  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

export const useOnboarding = () => {
  const value = useContext(OnboardingContext);
  if (!value)
    throw new Error("useOnboarding doit être utilisé dans <OnboardingProvider>");
  return value;
};

/** Données des scènes ; au-delà de 2 s ou en cas d'erreur, la séquence part en mode « Exemple ». */
export const useOnboardingStory = (enabled: boolean) =>
  useQuery({
    queryKey: ["onboarding_story"],
    enabled,
    retry: false,
    staleTime: 5 * 60 * 1000,
    // Revenir sur l'onglet ou retrouver le réseau ne relance pas la requête : la séquence ne repart pas à la scène 1.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async ({ signal }): Promise<Story | null> => {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), 2000);
      });
      const call = getSupabaseClient()
        .rpc("onboarding_story")
        .abortSignal(signal)
        .then(({ data, error }) => (error ? null : (data as Story)));
      return Promise.race([call, timeout]).finally(() => clearTimeout(timer));
    },
  });
